import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Signal from './Signal';
import { selectDeviceState, selectDeviceStateLoading } from "./deviceStateSlice";
import classes from './Signal.module.css';
import Spinner from "../ui/Spinner";
import { formatTimestamp, isEmpty } from "../../util/utils";

import {
    isMainStreetSignal,
    isPedestrianSignal,
    isTrolleyPedestrianSignal,
    isPrioritySignal,
    isTrolleySignal,
    isDriverSignal
} from '../trafficInfra/SignalTypes';
const rowBreak = (key) => (<div key={`break${key}`} className={classes.break} />);

export const SignalGroup = (props) => {
    const { intersection } = props;
    const deviceState = useSelector(selectDeviceState);
    const deviceStateLoading = useSelector(selectDeviceStateLoading);
    const topologies = useSelector(state => state.trafficInfra.topologies);
    const { t } = useTranslation();

    const streetMain = intersection['paikka'];
    const mainStreetTitle = streetMain ?? t('signal.main_street');
    const crossStreetTitle = t('signal.side_street');
    const spinner = deviceStateLoading ? <Spinner /> : null;
    const signalGroup = deviceState?.['signalGroup'] ?? [];

    // Topology matching logic
    const topology = topologies[intersection?.liva] || topologies[intersection?.liva_nro] || topologies[deviceState.device];

    const enrichSignalWithType = (signal) => {
        if (!topology || !topology.signals) return [signal]; // Return array

        const signalsList = Object.values(topology.signals);
        // Find ALL matches in topology for this signal group
        // Strict match: Label must start with name AND follow with digit (e.g. K -> K1, K2).
        // This prevents "K" matching "Ko1".
        const topologySignals = signalsList.filter(ts => {
            if (!ts.label && !ts.id) return false;
            const label = ts.label || ts.id;
            const regex = new RegExp(`^${signal.name}(\\d|$)`, 'i');
            return regex.test(label);
        });

        if (topologySignals.length > 0) {
            // Group by type to avoid duplicates (e.g. A1, A2, A3 are all "straight", show only one "straight")
            const uniqueTypes = [...new Set(topologySignals.map(s => s.type))];

            return uniqueTypes.map(type => ({
                ...signal,
                type: type,
                // Append type to key if we are splitting one signal into multiple to ensure unique keys
                idx: `${signal.idx}_${type}`
            }));
        }

        // Strict filtering: If topology exists but signal is not in it, do not render it.
        // This is crucial for shared devices (e.g. 601/602) where device state has signals for both,
        // but we only want to show signals for the selected intersection.
        return [];
    };

    const timestamp = !isEmpty(signalGroup) ? formatTimestamp(deviceState.timestamp) : null;
    const mainStreetSignals = signalGroup
        .filter(isMainStreetSignal)
        .flatMap(enrichSignalWithType)
        .map(signal => <Signal signal={signal} key={signal['idx']} />);
    const sideStreetSignals = signalGroup
        .filter(isDriverSignal)
        .filter(signal => !isMainStreetSignal(signal))
        .flatMap(enrichSignalWithType)
        .map(signal => <Signal signal={signal} key={signal['idx']} />);
    const pedestrianSignals = signalGroup
        .filter(signal => (isPedestrianSignal(signal) || isTrolleyPedestrianSignal(signal)))
        .map(signal => <Signal signal={signal} key={signal['idx']} />);
    const trolleySignals = signalGroup
        .filter(isTrolleySignal)
        .map(signal => <Signal signal={signal} key={signal['idx']} />);
    const prioritySignals = signalGroup
        .filter(isPrioritySignal)
        .map(signal => <Signal signal={signal} key={signal['idx']} />);

    return (
        <div className={classes.SignalGroup}>
            <div className={classes.SignalTitle} style={{ marginBottom: '5px' }}>
                {timestamp || '\u00A0'} {spinner}
            </div>
            {!isEmpty(mainStreetSignals) ?
                [<div key={'title'} className={classes.SignalTitle}>{mainStreetTitle}</div>, rowBreak(1), mainStreetSignals, rowBreak(2)] : null}
            {!isEmpty(sideStreetSignals) ?
                [<div key={'title'} className={classes.SignalTitle}>{crossStreetTitle}</div>, rowBreak(1), sideStreetSignals, rowBreak(2)] : null}
            {!isEmpty(pedestrianSignals) ?
                [<div key={'title'} className={classes.SignalTitle}>{t('signal.pedestrian')}</div>, rowBreak(1), pedestrianSignals, rowBreak(2)] : null}
            {!isEmpty(trolleySignals) ?
                [<div key={'title'} className={classes.SignalTitle}>{t('signal.tram')}</div>, rowBreak(1), trolleySignals, rowBreak(2)] : null}
            {!isEmpty(prioritySignals) ?
                [<div key={'title'} className={classes.SignalTitle}>{t('signal.priority')}</div>, rowBreak(1), prioritySignals, rowBreak(2)] : null}
        </div>
    );
};
